import { defineRule, configure, useForm } from 'vee-validate'
import { required, email, min, max, numeric, regex } from '@vee-validate/rules'
import { localize, setLocale } from '@vee-validate/i18n'
import en from '@vee-validate/i18n/dist/locale/en.json'

// Define rules
defineRule('required', required)
defineRule('email', email)
defineRule('min', min)
defineRule('max', max)
defineRule('numeric', numeric)
defineRule('regex', regex)

// Define the custom FEIN validation rule
export const feinFormat = (value, [status]) => {
  if (status === 'I') {
    return (
      /^(?:\d{2}-?\d{7})$/.test(value) ||
      'Please enter a valid FEIN/EIN number in the format XX-XXXXXXX'
    )
  }
  return true // No validation needed if status is not 'I'
}

const getNameWithoutSuffix = (name) => {
  const suffixes = ['jr', 'sr', 'ii', 'iii', 'iv', 'v']
  const nameParts = name.toLowerCase().split(/[\s,]+/)

  // Remove suffix if present
  if (suffixes.includes(nameParts[nameParts.length - 1].replace('.', ''))) {
    nameParts.pop()
  }
  return nameParts.join(' ')
}

const getNameSuffix = (name) => {
  const suffixes = ['jr', 'sr', 'ii', 'iii', 'iv', 'v']
  const lastPart = name.toLowerCase().split(/[\s,]+/).pop().replace('.', '')
  return suffixes.includes(lastPart) ? lastPart.toUpperCase() : null
}

// Define the signature match validation rule
export const signatureMatch = (value, [officers, signingOfficerId]) => {
  if (!value || !officers || !signingOfficerId) return true
  const selectedOfficer = officers.find(officer => officer.id === signingOfficerId)
  if (!selectedOfficer) return true

  // Skip validation if the officer type is Corporation ('C')
  if (selectedOfficer.officer_type === 'C') return true

  // Normalize the officer name and signature for comparison
  const normalizedOfficerName = selectedOfficer.name.toLowerCase().replace(/[.,]/g, '')
  const normalizedSignature = value.toLowerCase().replace(/[.,]/g, '')

  // Get the officer's name parts, filtering out empty parts
  const officerNameParts = normalizedOfficerName.split(/[\s,]+/).filter(part => part)

  // Get the input name parts, filtering out empty parts
  const inputNameParts = normalizedSignature.split(/[\s,]+/).filter(part => part)

  // Extract first and last name from officer name (assuming first and last are required)
  let officerFirstName = ''
  let officerLastName = ''
  let officerMiddleName = ''

  // Handle "Last, First" format
  if (selectedOfficer.name.includes(',')) {
    const [lastName, firstParts] = selectedOfficer.name.split(',').map(part => part.trim().toLowerCase())
    officerLastName = lastName.replace(/[.,]/g, '')

    // Split first parts to handle middle names
    const firstNameParts = firstParts.split(/\s+/)
    officerFirstName = firstNameParts[0].replace(/[.,]/g, '')

    // If there are more parts, consider them as middle name
    if (firstNameParts.length > 1) {
      officerMiddleName = firstNameParts.slice(1).join(' ').replace(/[.,]/g, '')
    }
  } else {
    // Assume format is "First [Middle] Last [Suffix]"
    officerFirstName = officerNameParts[0]

    if (officerNameParts.length > 2) {
      // If more than 2 parts, assume middle name exists
      officerMiddleName = officerNameParts.slice(1, -1).join(' ')
      officerLastName = officerNameParts[officerNameParts.length - 1]
    } else if (officerNameParts.length === 2) {
      // Just first and last
      officerLastName = officerNameParts[1]
    }
  }

  // More lenient validation - check if the signature contains parts of the name
  // or if the name contains parts of the signature
  const signatureContainsFirstName = normalizedSignature.includes(officerFirstName)
  const signatureContainsLastName = normalizedSignature.includes(officerLastName)

  // Check if any part of the input matches any part of the officer name
  const anyPartMatches = inputNameParts.some(inputPart =>
    officerNameParts.some(officerPart =>
      inputPart.includes(officerPart) || officerPart.includes(inputPart)
    )
  )

  if ((signatureContainsFirstName && signatureContainsLastName) || anyPartMatches) {
    return true
  }

  // If we get here, the signature doesn't match
  let message = `The signature doesn't match the officer name: ${selectedOfficer.name}.`
  message += ` Please include at least the first name "${officerFirstName}" and last name "${officerLastName}".`
  return message
}

defineRule('feinFormat', feinFormat)
defineRule('signatureMatch', signatureMatch)

// Set locale
localize({ en })
setLocale('en')

// Configure localized error messages
configure({
  generateMessage: localize({
    en: {
      fields: {
        email_address_confirmed: {
          required: 'Please confirm your email address.'
        },
        signing_officer_authorization: {
          required: 'Please confirm that you have read this agreement by checking the box or tapping above.'
        },
        signing_officer: {
          required: 'Please select an officer.'
        },
        signing_officer_signature: {
          required: (ctx) => {
            const selectedOfficer = ctx.form?.officers?.find(officer => officer.id === ctx.form?.signing_officer)
            let message = 'Please type your full name into the signature field above.'

            if (selectedOfficer) {
              if (selectedOfficer.officer_type === 'C') {
                message = 'Please enter an authorized signature for this corporation.'
              } else {
                message = `Please type at least your first and last name as they appear in: ${selectedOfficer.name}`
              }
            }

            return message
          }
        },
        fein_status: {
          required: 'Please select the status of your FEIN Number.'
        }
      },
      messages: {
        required: 'This field is required.',
        email: 'Please enter a valid email address.',
        numeric: 'Make sure to include only numbers in the {field}.'
      }
    }
  })
})

// A validatior to make sure no P.O. Box addresses are entered for registered agent address
defineRule('notPoBox', (value) => {
  const trimmedValue = value.trim()
  if (
    !trimmedValue ||
    trimmedValue.toLowerCase().startsWith('p.o.') ||
    trimmedValue.toLowerCase().startsWith('po box')
  ) {
    return 'P.O. Box addresses are not allowed.'
  }
  return true
})
